import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Spinner } from '@centraoneplus/ui';
import apiKeysService, {
  ApiKeyRequestLogEntry,
  REQUEST_LOG_PAGE_SIZE,
  REQUEST_LOG_RETENTION_DAYS,
} from '@services/api-keys.service';
import {
  StatusBadge,
  rowClass,
  sectionHeadingClass,
  tableClass,
  tableWrapClass,
  tdClass,
  thClass,
  theadClass,
} from './apiKeyInsightsUi';

/** Wait this long after the last keystroke before searching by request id. */
const SEARCH_DEBOUNCE_MS = 350;
/** How long the copy button shows its "copied" / "failed" state. */
const COPY_FEEDBACK_MS = 2000;
const REQUEST_ID_MAX_LENGTH = 100;
const COLUMN_COUNT = 9;

const mutedDash = <span className="text-gray-400 dark:text-gray-500">—</span>;

function describeLoadError(error: unknown): string {
  const data = (error as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  const message = data?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && message.length > 0) return message.join(', ');
  if (data?.status === 404) return 'This API key no longer exists.';
  return 'The request log could not be loaded.';
}

function mergeEntries(
  current: ApiKeyRequestLogEntry[],
  next: ApiKeyRequestLogEntry[],
): ApiKeyRequestLogEntry[] {
  // A request landing exactly on the cursor timestamp could be served twice.
  const seen = new Set(current.map((entry) => entry.id));
  return [...current, ...next.filter((entry) => !seen.has(entry.id))];
}

/**
 * Recent requests made with one API key, newest first — which calls were
 * made, which failed and why. Request and response bodies (and query strings)
 * are never recorded, so there is nothing sensitive to reveal here beyond the
 * path, the caller's IP and the public error the caller already received.
 */
export default function ApiKeyRequestLog({
  apiKeyId,
  onRefresh,
}: {
  apiKeyId: string;
  /** Called when the admin presses Refresh, so the totals above can reload too. */
  onRefresh?: () => void;
}) {
  const [entries, setEntries] = useState<ApiKeyRequestLogEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const [errorsOnly, setErrorsOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [requestId, setRequestId] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<{ id: string; ok: boolean } | null>(null);

  // Bumped for every first-page load, so a slow response for an older filter
  // (or an older "load more") can never overwrite what is on screen now.
  const generation = useRef(0);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const myGeneration = ++generation.current;
    setLoading(true);
    setLoadError(null);
    setLoadMoreError(null);

    apiKeysService
      .getApiKeyRequests(apiKeyId, {
        errorsOnly,
        requestId: requestId || undefined,
        limit: REQUEST_LOG_PAGE_SIZE,
      })
      .then((page) => {
        if (myGeneration !== generation.current) return;
        setEntries(page.data);
        setNextBefore(page.nextBefore);
        setLoading(false);
      })
      .catch((error) => {
        if (myGeneration !== generation.current) return;
        // Drop the rows: they may belong to a different filter.
        setEntries([]);
        setNextBefore(null);
        setLoadError(describeLoadError(error));
        setLoading(false);
      });

    return () => {
      generation.current++;
    };
  }, [apiKeyId, errorsOnly, requestId, reloadToken]);

  // Search as the admin types (or pastes) a request id, without a call per key.
  useEffect(() => {
    const timer = setTimeout(() => setRequestId(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const loadMore = useCallback(async () => {
    if (!nextBefore || loadingMore) return;
    const myGeneration = generation.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await apiKeysService.getApiKeyRequests(apiKeyId, {
        errorsOnly,
        requestId: requestId || undefined,
        limit: REQUEST_LOG_PAGE_SIZE,
        before: nextBefore,
      });
      if (myGeneration !== generation.current) return;
      setEntries((current) => mergeEntries(current, page.data));
      setNextBefore(page.nextBefore);
    } catch (error) {
      if (myGeneration !== generation.current) return;
      setLoadMoreError(describeLoadError(error));
    } finally {
      setLoadingMore(false);
    }
  }, [apiKeyId, errorsOnly, requestId, nextBefore, loadingMore]);

  const handleRefresh = () => {
    setReloadToken((token) => token + 1);
    onRefresh?.();
  };

  const handleCopy = async (entry: ApiKeyRequestLogEntry) => {
    if (!entry.requestId) return;
    let ok = true;
    try {
      await navigator.clipboard.writeText(entry.requestId);
    } catch {
      // Clipboard access is refused outside a secure context or without focus.
      ok = false;
    }
    setCopyState({ id: entry.id, ok });
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopyState(null), COPY_FEEDBACK_MS);
  };

  const filtered = errorsOnly || requestId !== '';
  const errorCount = entries.filter((entry) => entry.statusCode >= 400).length;
  const showInitialSpinner = loading && entries.length === 0 && !loadError;

  return (
    <section aria-label="Recent requests">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-2">
        <div>
          <h4 className={sectionHeadingClass}>Recent requests</h4>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Request and response bodies are not recorded. Kept for {REQUEST_LOG_RETENTION_DAYS}{' '}
            days.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            <button
              type="button"
              role="switch"
              aria-checked={errorsOnly}
              aria-label="Errors only"
              onClick={() => setErrorsOnly((value) => !value)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errorsOnly ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                  errorsOnly ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
            <span aria-hidden>Errors only</span>
          </label>

          <form
            role="search"
            className="relative"
            onSubmit={(event) => {
              event.preventDefault();
              setRequestId(searchInput.trim());
            }}
          >
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 dark:text-gray-500"
              aria-hidden
            />
            <input
              type="text"
              value={searchInput}
              maxLength={REQUEST_ID_MAX_LENGTH}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Find by request ID"
              aria-label="Find by request ID"
              autoComplete="off"
              spellCheck={false}
              className="w-56 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white py-1.5 pl-7 pr-7 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setRequestId('');
                }}
                aria-label="Clear request ID search"
                title="Clear"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh requests"
            title="Refresh"
            className="rounded-lg border border-gray-300 dark:border-gray-600 p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {showInitialSpinner ? (
        <div className="flex h-24 items-center justify-center" role="status" aria-label="Loading requests">
          <Spinner size="sm" />
        </div>
      ) : loadError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span className="flex-1">{loadError}</span>
          <button
            type="button"
            onClick={handleRefresh}
            className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-900/40"
          >
            Try again
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 px-3 py-5 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {requestId
              ? `No request with ID “${requestId}” for this key${errorsOnly ? ' among failed requests' : ''}.`
              : errorsOnly
                ? `No failed requests in the last ${REQUEST_LOG_RETENTION_DAYS} days.`
                : `No requests recorded for this key in the last ${REQUEST_LOG_RETENTION_DAYS} days.`}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {requestId
              ? `Request IDs are matched exactly, and requests older than ${REQUEST_LOG_RETENTION_DAYS} days are deleted.`
              : 'Requests sent with an invalid key cannot be matched to a key, so they are not listed here.'}
          </p>
        </div>
      ) : (
        <>
          <div className={tableWrapClass} aria-busy={loading}>
            <table className={`${tableClass} ${loading ? 'opacity-60' : ''}`}>
              <thead className={theadClass}>
                <tr>
                  <th scope="col" className="w-6 px-1 py-2">
                    <span className="sr-only">Details</span>
                  </th>
                  <th scope="col" className={thClass}>Time</th>
                  <th scope="col" className={thClass}>Method</th>
                  <th scope="col" className={thClass}>Path</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={thClass}>Error</th>
                  <th scope="col" className={`${thClass} text-right`}>Duration</th>
                  <th scope="col" className={thClass}>IP</th>
                  <th scope="col" className={thClass}>Request ID</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const isError = entry.statusCode >= 400;
                  const isExpanded = expandedId === entry.id;
                  const copied = copyState?.id === entry.id ? copyState.ok : null;
                  return (
                    <Fragment key={entry.id}>
                      <tr
                        data-testid="request-row"
                        data-error={isError ? 'true' : 'false'}
                        className={`${rowClass} ${isError ? 'bg-red-50/70 dark:bg-red-900/10' : ''}`}
                      >
                        {/* Red edge on failed requests so they stand out when scanning. */}
                        <td
                          className={`border-l-2 px-1 py-2 align-top ${
                            isError ? 'border-l-red-500' : 'border-l-transparent'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Hide request details' : 'Show request details'}
                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </td>
                        <td
                          className={`${tdClass} whitespace-nowrap`}
                          title={apiKeysService.formatExactTime(entry.occurredAt)}
                        >
                          <time dateTime={entry.occurredAt}>
                            {apiKeysService.formatRelativeTime(entry.occurredAt)}
                          </time>
                        </td>
                        <td className={`${tdClass} font-mono font-semibold`}>{entry.method}</td>
                        <td className={`${tdClass} font-mono`}>
                          <span className="block max-w-[18rem] truncate" title={entry.path}>
                            {entry.path}
                          </span>
                        </td>
                        <td className={tdClass}>
                          <StatusBadge statusCode={entry.statusCode} />
                        </td>
                        <td className={tdClass}>
                          {entry.errorCode || entry.errorMessage ? (
                            <div className="max-w-[20rem]">
                              {entry.errorCode && (
                                <div className="font-mono font-semibold text-red-700 dark:text-red-300 break-all">
                                  {entry.errorCode}
                                </div>
                              )}
                              {entry.errorMessage && (
                                <div
                                  className="truncate text-gray-600 dark:text-gray-400"
                                  title={entry.errorMessage}
                                >
                                  {entry.errorMessage}
                                </div>
                              )}
                            </div>
                          ) : (
                            mutedDash
                          )}
                        </td>
                        <td className={`${tdClass} text-right tabular-nums whitespace-nowrap`}>
                          {apiKeysService.formatDuration(entry.durationMs)}
                        </td>
                        <td className={`${tdClass} font-mono whitespace-nowrap`}>
                          {entry.ipAddress ?? mutedDash}
                        </td>
                        <td className={tdClass}>
                          {entry.requestId ? (
                            <div className="flex items-center gap-1">
                              <span
                                className="block max-w-[11rem] truncate font-mono"
                                title={entry.requestId}
                              >
                                {entry.requestId}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleCopy(entry)}
                                aria-label={`Copy request ID ${entry.requestId}`}
                                title={
                                  copied === true
                                    ? 'Copied'
                                    : copied === false
                                      ? 'Could not copy — select the ID and copy it manually'
                                      : 'Copy request ID'
                                }
                                className={`flex-shrink-0 rounded p-1 ${
                                  copied === true
                                    ? 'text-green-600 dark:text-green-400'
                                    : copied === false
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                                }`}
                              >
                                {copied === true ? (
                                  <Check className="h-3.5 w-3.5" />
                                ) : copied === false ? (
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <span className="sr-only" role="status">
                                {copied === true
                                  ? 'Request ID copied'
                                  : copied === false
                                    ? 'Could not copy the request ID'
                                    : ''}
                              </span>
                            </div>
                          ) : (
                            mutedDash
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={isError ? 'bg-red-50/40 dark:bg-red-900/5' : ''}>
                          <td colSpan={COLUMN_COUNT} className="px-3 pb-3 pt-1">
                            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 pl-6 text-xs">
                              <dt className="text-gray-500 dark:text-gray-400">Time</dt>
                              <dd className="text-gray-900 dark:text-white">
                                {apiKeysService.formatExactTime(entry.occurredAt)}{' '}
                                <span className="font-mono text-gray-500 dark:text-gray-400">
                                  ({entry.occurredAt})
                                </span>
                              </dd>
                              <dt className="text-gray-500 dark:text-gray-400">Request</dt>
                              <dd className="font-mono text-gray-900 dark:text-white break-all">
                                {entry.method} {entry.path}
                              </dd>
                              <dt className="text-gray-500 dark:text-gray-400">Endpoint</dt>
                              <dd className="font-mono text-gray-900 dark:text-white break-all">
                                {entry.endpoint}
                              </dd>
                              {(entry.errorCode || entry.errorMessage) && (
                                <>
                                  <dt className="text-gray-500 dark:text-gray-400">Error</dt>
                                  <dd className="text-gray-900 dark:text-white break-words whitespace-pre-wrap">
                                    {entry.errorCode && (
                                      <span className="font-mono font-semibold text-red-700 dark:text-red-300">
                                        {entry.errorCode}
                                      </span>
                                    )}
                                    {entry.errorCode && entry.errorMessage ? ' — ' : ''}
                                    {entry.errorMessage}
                                  </dd>
                                </>
                              )}
                              {entry.requestId && (
                                <>
                                  <dt className="text-gray-500 dark:text-gray-400">Request ID</dt>
                                  <dd className="font-mono text-gray-900 dark:text-white break-all">
                                    {entry.requestId}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>
              Showing {entries.length.toLocaleString()}{' '}
              {filtered ? 'matching ' : ''}
              {entries.length === 1 ? 'request' : 'requests'}
              {!errorsOnly && errorCount > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {errorCount.toLocaleString()} failed
                  </span>
                </>
              )}
              {!nextBefore && ' · end of log'}
            </span>
            <div className="flex items-center gap-2">
              {loadMoreError && (
                <span role="alert" className="text-red-600 dark:text-red-400">
                  {loadMoreError}
                </span>
              )}
              {nextBefore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore || loading}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {loadingMore && <Spinner size="sm" />}
                  {loadMoreError ? 'Retry' : 'Load more'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
